<template>
  <div class="poi-map-container">
    <GmapMap
      :zoom="12"
      :center="mapCenter"
      :options="{
        mapTypeControl: false,
        streetViewControl: false
      }"
      v-bind:class="{ 'directions-error': hasError}"
      map-type-id="roadmap"
      ref="mapRef"
    >
      <GmapMarker
        :position="{ lat: poi.lat, lng: poi.lon }"
        :clickable="markerPopup"
        :draggable="false"
        v-for="poi in poiList"
        :key="poi.id"
        @click="openInfoWindowTemplate(poi)"
      />
      <GmapMarker
        :position="{ lat: position.lat, lng: position.lon }"
        :clickable="false"
        :draggable="false"
        :icon="{
          path: `M 0, 0
                  m -12, 0
                  a 12,12 0 1,0 24,0
                  a 12,12 0 1,0 -24,0`,
          fillColor: '#3075F0',
          fillOpacity: 1,
          strokeColor: '#3075F0',
          scale: 0.5
        }"
        v-if="position"
      />
      <GmapInfoWindow
        :options="{maxWidth: 320}"
        :position="infoWindow.position"
        :opened="infoWindow.open"
        @closeclick="infoWindow.open=false">
        <div v-html="infoWindow.template"></div>
      </GmapInfoWindow>
    </GmapMap>
    <div class="directions-error" v-show="hasError">
      <div>Unfortunately this tour can't be routed by Google. Please, try another one or <a href="#" @click="hasError = false">try again</a></div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'

import { gmapApi } from 'vue2-google-maps'

export default {
  name: 'PoiMap',
  props: {
    poiList: {
      type: Array,
      default: () => []
    },
    position: {
      default: undefined
    },
    center: {
      type: Object,
      default: undefined
    },
    routingType: {
      type: Number,
      default: 1
    },
    directions: {
      type: Boolean,
      default: false
    },
    markerPopup: {
      type: Boolean,
      default: false
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      directionsDisplay: null,
      hasError: false,
      inRequest: false,
      infoWindow: {
        position: { lat: 0, lng: 0 },
        open: false,
        template: ''
      }
    }
  },
  methods: {
    ...mapMutations(['startLoad', 'endLoad']),
    update: function () {
      if (!this.inRequest) {
        this.inRequest = true
        setTimeout(this.startLoad, 2000)
        this.getDirections().catch(() => {}).finally(() => {
          this.inRequest = false
          this.endLoad()
        })
      }
    },
    createWaypoints: function (poiList) {
      // Converting a user selected POI list to Maps API waypoint format
      return poiList.map(x => {
        return {
          location: new this.google.maps.LatLng(
            x.lat,
            x.lon
          ),
          stopover: true
        }
      })
    },
    createLocationList: function (poiList) {
      // Converting a user selected POI list to Maps API location list format
      return poiList.map(x => new this.google.maps.LatLng(x.lat, x.lon))
    },
    createDirectionsRequest: async function (origin, poiList, routing) {
      if (routing === 0) { // Fixed POI order routing
        // Converting a user selected POI list to Maps API waypoint format
        let waypoints = this.createWaypoints(poiList)

        // The farthest POI will be our destination for the route
        let destination = waypoints.pop().location

        return {
          request: {
            origin: origin,
            destination: destination,
            waypoints: waypoints,
            optimizeWaypoints: false,
            travelMode: 'WALKING'
          },
          poiList: poiList
        }
      } else if (routing === 1) { // Optimize tour route by distance
        let sortedList

        try {
          // Sorting POI list by distance to the user
          sortedList = await this.sortPoiList(poiList)
        } catch (error) {
          return null
        }

        // Converting a user selected POI list to Maps API waypoint format
        let waypoints = this.createWaypoints(sortedList)

        // The farthest POI will be our destination for the route
        let destination = waypoints.pop().location

        return {
          request: {
            origin: origin,
            destination: destination,
            waypoints: waypoints,
            optimizeWaypoints: true,
            travelMode: 'WALKING'
          },
          poiList: sortedList
        }
      } else if (routing === 2) { // Find the closest POI and loop the tour
        let sortedList

        try {
          // Sorting POI list by distance to the user
          sortedList = await this.sortPoiList(poiList)
        } catch (error) {
          return null
        }

        let closestIndex = poiList.findIndex((elem) => elem.id === sortedList[0].id)

        let shiftedList = poiList.slice(closestIndex, poiList.length).concat(poiList.slice(0, closestIndex))

        // Converting a user selected POI list to Maps API waypoint format
        let waypoints = this.createWaypoints(shiftedList)

        // The farthest POI will be our destination for the route
        let destination = waypoints.pop().location

        return {
          request: {
            origin: origin,
            destination: destination,
            waypoints: waypoints,
            optimizeWaypoints: false,
            travelMode: 'WALKING'
          },
          poiList: shiftedList
        }
      } else {
        return null
      }
    },
    sortPoiList: function (poiList) {
      return new Promise((resolve, reject) => {
        let distanceService = new this.google.maps.DistanceMatrixService()
        let origin = new this.google.maps.LatLng(this.position.lat, this.position.lon)
        let locationList = this.createLocationList(poiList)

        distanceService.getDistanceMatrix({
          origins: [origin],
          destinations: locationList,
          travelMode: 'WALKING',
          unitSystem: this.google.maps.UnitSystem.IMPERIAL
        }, (response, status) => {
          if (status === 'OK') {
            // Safari doesn't work correctly with .indexOf(a) method,
            // were added index to object to get around .indexOf(a) method
            let resultList = Array.from(poiList).map((item, index) => {
              item.index = index
              return item
            })

            // Sorting result by distance values received from Google
            resolve(resultList.sort((a, b) => {
              let responseA = response.rows[0].elements[a.index]
              let responseB = response.rows[0].elements[b.index]

              if (responseA.status === 'OK' && responseB.status === 'OK') {
                return responseA.distance.value - responseB.distance.value
              } else {
                return 0
              }
            }))
          } else {
            reject(status)
          }
        })
      })
    },
    getDirections: function () {
      if (this.hasError || !this.directions || !this.mapAllowed) {
        return new Promise((resolve) => { resolve() })
      }

      return new Promise(async (resolve, reject) => {
        await this.$gmapApiPromiseLazy()
        // Maps API Services init
        let directionsService = new this.google.maps.DirectionsService()

        if (this.directionsDisplay) {
          this.directionsDisplay.set('directions', null)
          this.directionsDisplay.setOptions({ preserveViewport: true })
        } else {
          this.directionsDisplay = new this.google.maps.DirectionsRenderer({ suppressMarkers: true })

          this.$refs.mapRef.$mapPromise.then((map) => {
            this.directionsDisplay.setMap(map)
            this.directionsDisplay.setPanel(this.$refs.directions)
          })
        }

        // Constructing the directions API request
        let origin = new this.google.maps.LatLng(this.position.lat, this.position.lon)

        let requestProxy = await this.createDirectionsRequest(origin, this.poiList, this.routingType)

        if (requestProxy === null) {
          reject(new Error("Couldn't create the directions request"))
        }

        let request = requestProxy.request
        let poiList = requestProxy.poiList

        // Executing the directions API request
        directionsService.route(request, (response, status) => {
          // eslint-disable-next-line no-undef
          if (status === this.google.maps.GeocoderStatus.OK) {
            this.directionsDisplay.setDirections(response)

            // Building our route from the user selected POI list according to the optimized order suggested by Maps API
            let route = []

            for (let i = 0; i < response.routes[0].waypoint_order.length; i++) {
              let index = response.routes[0].waypoint_order[i]

              route.push(poiList[index])
            }

            // Our destination will be the last step of the route
            route.push(poiList[poiList.length - 1])

            resolve(route)
          } else {
            this.hasError = true
            reject(new Error(status))
          }
        })
      })
    },
    openInfoWindowTemplate: function (poi) {
      this.infoWindow.template = `
        <h3>${poi.name.en}</h3>
        <p>${poi.information.en}</p>
        <a style="color:#000000;text-decoration:underline;"
           href="/poi/${poi.id}/directions">
           More information
        </a>`
      this.infoWindow.position = { lat: poi.lat, lng: poi.lon }
      this.infoWindow.open = true
    }
  },
  computed: {
    google: gmapApi,
    mapAllowed: function () {
      return this.poiList !== undefined && this.poiList.length > 0 && this.position !== undefined
    },
    mapCenter: function () {
      if (this.center) {
        return this.center
      } else if (this.position) {
        return { lat: this.position.lat, lng: this.position.lon }
      } else if (this.poiList.length > 0) {
        return { lat: this.poiList[0].lat, lng: this.poiList[0].lon }
      } else {
        return { lat: 0, lng: 0 }
      }
    }
  },
  watch: {
    position: function () {
      if (this.visible) {
        this.update()
      }
    },
    poiList: function (newVal) {
      if (newVal && newVal.length > 0 && this.visible) {
        this.update()
      }
    },
    directions: function () {
      if (this.visible) {
        this.update()
      }
    },
    visible: function (newVal) {
      if (newVal) {
        this.update()
      }
    },
    $route: function (next, prev) {
      if (this.directionsDisplay) {
        this.directionsDisplay.set('directions', null)
        this.directionsDisplay = null
      }
    }
  }
}
</script>
